import React, { useEffect } from 'react';
import { Field } from 'ui';
import ListFlowInfoDescription from '~/components/ListFlow/Descriptions/ListFlowInfoDescription';
import CategorySelector from '~/components/ListFlow/InfoSection/CategorySelector';
import ListFlowDetailSelections from '~/components/ListFlow/InfoSection/ListFlowDetailSelections';
import ListFlowQuantityPicker from '~/components/ListFlow/InfoSection/ListFlowQuantityPicker';
import ListFlowTitleAndDescription from '~/components/ListFlow/InfoSection/ListFlowTitleAndDescription';
import ShipsFromCountryPicker from '~/components/ListFlow/InfoSection/ShipsFromCountryPicker';
import ListFlowVariationsAddOptionButton from '~/components/ListFlow/InfoSection/Variations/ListFlowVariationsAddOptionButton';
import ListFlowVariationsTable from '~/components/ListFlow/InfoSection/Variations/ListFlowVariationsTable';
import ListFlowSection from '~/components/ListFlow/ListFlowSection';
import RequiredStar from '~/components/ListFlow/RequiredStar';
import {
  useListFlowCategories,
  useListFlowCategory,
} from '~/hooks/listFlow/useListFlowCategories';
import useHasFeature from '~/hooks/useHasFeature';
import {
  changeCategory,
  getListFlowCategory,
  initializeCategory,
  isListFlowShowingVariations,
} from '~/store/listFlow/slices/info';
import { getListFlowFieldErrorMap } from '~/store/listFlow/ui';
import { useAppDispatch, useAppSelector } from '~/store/store';

interface Props {}

const ListFlowInfoSection: React.FC<Props> = () => {
  const dispatch = useAppDispatch();
  const errorMap = useAppSelector(getListFlowFieldErrorMap);
  const category = useAppSelector(getListFlowCategory);
  const showingVariations = useAppSelector(isListFlowShowingVariations);
  const { data: categories } = useListFlowCategories();
  const hasVariations = useHasFeature('list_variations');

  const { data } = useListFlowCategory(category?.id);

  useEffect(() => {
    if (data) {
      dispatch(initializeCategory(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <ListFlowSection title="Info" description={<ListFlowInfoDescription />}>
      <ShipsFromCountryPicker />

      <Field
        label={
          <span>
            Category <RequiredStar />
          </span>
        }
        id="category"
        error={errorMap?.category}
      >
        <CategorySelector
          id="category"
          categories={categories}
          value={category || null}
          onChange={newCategory => dispatch(changeCategory(newCategory))}
        />
      </Field>

      {category && <ListFlowDetailSelections category={category} />}

      <Field label="Quantity" id="quantity" error={errorMap?.quantity}>
        <div className="flex items-center space-x-4">
          <ListFlowQuantityPicker />

          {hasVariations && <ListFlowVariationsAddOptionButton />}
        </div>
      </Field>

      {showingVariations && <ListFlowVariationsTable />}

      <ListFlowTitleAndDescription />
    </ListFlowSection>
  );
};

export default ListFlowInfoSection;
